import React, {useCallback, useEffect, useRef, useState} from 'react';
import api, { getUploadStatus, uploadStatement } from '../utils/api';
import Header from './common/Header';
import Sidebar from "./common/Sidebar";
import { useTranslation } from 'react-i18next';

const POLL_MS = 2000;
const MAX_POLLS = 150; // 5 minutos

const FileUpload = () => {
  const { t } = useTranslation();
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [recentUploads, setRecentUploads] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const mounted = useRef(true);

  const loadRecent = useCallback(async () => {
    try {
      const res = await api.get('/uploads?limit=6');
      if (mounted.current) setRecentUploads(res.data.recent_uploads || []);
    } catch (e) {
      console.error('No se pudieron cargar las subidas recientes', e);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    loadRecent();
    return () => { mounted.current = false; };
  }, [loadRecent]);

  // El procesamiento corre en una Edge Function; acá sólo se consulta el estado.
  const waitForProcessing = useCallback(async (uploadId) => {
    for (let i = 0; i < MAX_POLLS && mounted.current; i++) {
      await new Promise((resolve) => setTimeout(resolve, POLL_MS));
      const upload = await getUploadStatus(uploadId);
      if (mounted.current) {
        setRecentUploads((prev) => prev.map((u) => (u.id === uploadId ? { ...u, ...upload } : u)));
      }
      if (upload.status === 'processed' || upload.status === 'error') return upload;
    }
    return null;
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (files.length === 0) {
      setError(t('upload.errorNoFile', 'Please select a file before uploading.'));
      return;
    }
    setError(null);
    setIsUploading(true);
    const errors = [];
    const started = [];
    for (const file of files) {
      try {
        started.push(await uploadStatement(file));
      } catch (e) {
        errors.push(`${file.name}: ${e.response?.data?.error || e.message}`);
      }
    }
    setFiles([]);
    setIsUploading(false);
    if (errors.length) setError(errors.join(' · '));
    await loadRecent();
    await Promise.all(started.map(waitForProcessing));
  };

  const statusLabel = (upload) => {
    switch (upload.status) {
      case 'pending':
      case 'processing':
        return t('upload.statusProcessing', 'Procesando…');
      case 'processed':
        return t('upload.statusProcessed', '{{count}} movimientos', { count: upload.rows_processed ?? 0 });
      case 'error':
        return upload.error || t('upload.statusError', 'Error al procesar');
      default:
        return '';
    }
  };

  const removeFile = (indexToRemove) => {
    setFiles(prevFiles => prevFiles.filter((_, index) => index !== indexToRemove));
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const dropped = Array.from(e.dataTransfer?.files || []);
    if (dropped.length) setFiles((prev) => [...prev, ...dropped]);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isDragging) setIsDragging(false);
  };

  return (
    <div className="dashboard-container">
      <Header title={t('upload.title', 'Upload')} />
      <div className="dashboard-two-columns">
        <Sidebar />

        <div className="main-content upload-main-content">
          <div className="dashboard-section">
            <div className="upload-flex-container">
              <div className="upload-form-card">
                <h2>{t('upload.titleForm', 'Upload File')}</h2>
                <form className="file-upload-form" onSubmit={handleSubmit}>
                  <div
                    className={`upload-dropzone ${isDragging ? 'is-dragging' : ''}`}
                    onDrop={onDrop}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                  >
                    <div className="dropzone-inner">
                      <svg width="36" height="36" viewBox="0 0 24 24" aria-hidden="true">
                        <path fill="currentColor" d="M19 13v6H5v-6H3v8h18v-8h-2zm-6-9l-5 5h3v6h4V9h3l-5-5z"/>
                      </svg>
                      <p className="dropzone-text">
                        {t('upload.dragOrClick', 'Arrastra y suelta archivos aquí, o ')}
                        <label htmlFor="fileInput" className="dropzone-link">{t('upload.browse', 'cargar')}</label>
                      </p>
                      <input
                        id="fileInput"
                        type="file"
                        className="visually-hidden"
                        multiple
                        accept=".pdf,.xlsx,.xls,.csv"
                        onChange={(event) => {
                          const picked = Array.from(event.target.files || []);
                          if (picked.length) setFiles((prev) => [...prev, ...picked]);
                          event.target.value = '';
                        }}
                      />
                    </div>
                  </div>

                  {files.length > 0 && (
                    <div className="file-chips">
                      {files.map((file, index) => (
                        <span key={index} className="file-chip">
                          <span className="file-chip-name">{file.name}</span>
                          <button
                            type="button"
                            className="file-chip-remove"
                            onClick={() => removeFile(index)}
                            aria-label={t('upload.remove', 'Eliminar')}
                            title={t('upload.remove', 'Eliminar')}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="upload-actions">
                    <button type="submit" className="btn-primary" disabled={isUploading}>
                      {isUploading ? t('upload.uploading', 'Subiendo…') : t('upload.submit', 'Upload')}
                    </button>
                  </div>
                  {error && <p className="error-message">{error}</p>}
                </form>
              </div>
              <div className="recent-uploads-card">
                <h2>{t('upload.recentUploads', 'Recent Uploads')}</h2>
                <ul className="recent-uploads-list">
                  {recentUploads.slice(0, 6).map((upload) => (
                    <li key={upload.id} title={upload.error || ''}>
                      {upload.filename}
                      <span className={`upload-status upload-status-${upload.status}`}> — {statusLabel(upload)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileUpload;